import {
  API_TYPES,
  type ContractorBadgeVerificationStatus,
  updateContractorBadgeVerificationBodySchema,
  type UpdateContractorBadgeVerificationResponse,
  updateContractorBadgeVerificationResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { patch } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { CONTRACTOR_BADGE_VERIFICATIONS_URL } from "../constants";
import { invalidateContractorBadgeVerification } from "./useGetContractorBadgeVerification";

interface UpdateContractorBadgeVerificationParams {
  verificationId: string;
  status: ContractorBadgeVerificationStatus;
  shiftId: string;
}

type Options = UseMutationOptions<
  UpdateContractorBadgeVerificationResponse,
  AxiosError,
  UpdateContractorBadgeVerificationParams
>;

/**
 * Hook to update a contractor badge verification status.
 *
 * Use this to confirm a photo upload (status="uploaded") or to skip
 * verification (status="skipped").
 *
 * For "uploaded" status, the server verifies the file exists in S3
 * before setting the uploadedAt timestamp.
 */
export function useUpdateContractorBadgeVerification(
  options: Options = {}
): UseMutationResult<
  UpdateContractorBadgeVerificationResponse,
  AxiosError,
  UpdateContractorBadgeVerificationParams
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      verificationId,
      status,
      shiftId,
    }: UpdateContractorBadgeVerificationParams) => {
      const response = await patch({
        url: `${CONTRACTOR_BADGE_VERIFICATIONS_URL}/${verificationId}`,
        data: {
          data: {
            type: API_TYPES.contractorBadgeVerification,
            id: verificationId,
            attributes: {
              status,
            },
          },
        },
        requestSchema: updateContractorBadgeVerificationBodySchema,
        responseSchema: updateContractorBadgeVerificationResponseSchema,
      });

      // Invalidate GET cache so UI reflects the new status
      // Await to ensure cache is updated before mutation resolves
      await invalidateContractorBadgeVerification(queryClient, shiftId);

      // Zod parsing strips branded types, so we cast back to the expected type
      return response.data as UpdateContractorBadgeVerificationResponse;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.BADGE_VERIFICATION_UPDATE_FAILURE,
      ...options.meta,
    },
    ...options,
  });
}
